import { QueryClient } from "@tanstack/react-query";
import { Col, Dropdown, Space, notification, Button, Popconfirm } from "antd";
import { useEffect, useState } from "react";
import { DeleteOutlined } from "@ant-design/icons";
import InvoicePriceDrawer from "./InvoicePrintDrawer";
import { IGetInvoiceDetailsResponse } from "api/requests";
import CreateInvoicePaymentModal from "./CreateInvoicePaymentModal";
import { useAbility } from "@casl/react";
import { AbilityContext } from "utils/abilit";
import { ExtraPermissionType, SubjectKey } from "utils/enums";
import { useSalesServiceCancelInvoice } from "api/queries";

export interface IInvoiceViewHeaderOptionsProps {
  status: any;
  queryClient: QueryClient;
  invoiceId: number;
  data: IGetInvoiceDetailsResponse;
}

function InvoiceViewHeaderOptions(props: IInvoiceViewHeaderOptionsProps) {
  const [showPrintableInvoice, setShowPrintableInvoice] =
    useState<boolean>(false);
  const [createPaymentModalOpen, setCreatePaymentModalOpen] =
    useState<boolean>(false);
  const [dropDownItems, setDropDownItems] = useState<any>([]);

  const ability = useAbility(AbilityContext);

  const { mutate: cancelInvoiceMutation, isPending: isCancelPending } =
    useSalesServiceCancelInvoice({
      onSuccess: () => {
        notification.success({
          message: "Invoice cancelled successfully",
        });
        props.queryClient.invalidateQueries();
      },
      onError: (error: any) => {
        notification.error({
          description:
            error?.body?.message ||
            error?.response?.data?.message ||
            error?.message,
          message: "Failed to cancel invoice",
        });
      },
    });

  const handleOpenPrintDrawer = () => setShowPrintableInvoice(true);
  const handleClosePrintDrawer = () => setShowPrintableInvoice(false);

  const handleOpenCreatePaymentModal = () => setCreatePaymentModalOpen(true);
  const handleCloseCreatePaymentModal = () => setCreatePaymentModalOpen(false);

  useEffect(() => {
    const prevItems = [];
    if (
      props.data?.invoiceStatus === "SENT" ||
      props.data?.invoiceStatus === "PARTIAL" ||
      props.data?.invoiceStatus === "OVER_DUE"
    ) {
      prevItems.push({
        key: "create-payment",
        label: "Create Payment",
        onClick: handleOpenCreatePaymentModal,
      });
      setDropDownItems(prevItems);
    }
  }, [props.data]);

  return (
    <Col xs={12}>
      <Space
        align="center"
        direction="horizontal"
        // size="middle"
        style={{ justifyContent: "end", width: "100%" }}
      >
        {props.data?.invoiceStatus !== "CANCELED" &&
          ability.can(ExtraPermissionType.MANAGE, SubjectKey.INVOICE) && (
            <Popconfirm
              title="Cancel Invoice"
              description="Are you sure you want to cancel this invoice? This action cannot be undone."
              onConfirm={() =>
                cancelInvoiceMutation({ idInvoice: props.invoiceId })
              }
              okText="Yes, Cancel"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                loading={isCancelPending}
                icon={<DeleteOutlined />}
              >
                Cancel Invoice
              </Button>
            </Popconfirm>
          )}
        <Dropdown.Button
          menu={{
            items: dropDownItems,
          }}
          onClick={handleOpenPrintDrawer}
        >
          Print
        </Dropdown.Button>
      </Space>

      <InvoicePriceDrawer
        onClose={handleClosePrintDrawer}
        open={showPrintableInvoice}
        data={props.data}
      />

      <CreateInvoicePaymentModal
        onClose={handleCloseCreatePaymentModal}
        open={createPaymentModalOpen}
        data={props.data}
        queryClient={props.queryClient}
      />
    </Col>
  );
}

export default InvoiceViewHeaderOptions;
